/* eslint-disable jsx-a11y/control-has-associated-label */

/* eslint-disable react/no-array-index-key */
import { useSpring } from '@react-spring/core';
import { animated } from '@react-spring/web';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';

import useKeyboardKeys, { KEYS } from '../../hooks/useKeyboardKeys';

import { useWindowSize, useHeader, useLoading } from '../../contexts/SiteContext';
import { useFloatingVideo } from '../../contexts/VideoContext';
import CloseButton from '../buttons/Close';
import PlayIcon from '../icons/Play';
import Embed from './Embed';

// import * as AppPropTypes from '../../lib/PropTypes';
import styles from '../../../styles/partials/floating-video.module.scss';

const propTypes = {
    debug: PropTypes.bool,
    defaultThreshold: PropTypes.number,
    className: PropTypes.string,
};

const defaultProps = {
    debug: false,
    defaultThreshold: 0.2,
    className: null,
};

const FloatingVideo = ({ debug, defaultThreshold, className }) => {
    // IMPORTANT: avoid setState in this function, it will create glitches
    const apiRef = useRef(null);
    const { width: windowWidth = 0, height: windowHeight = 0 } = useWindowSize();
    const { setHeaderHidden, setScrollDirection, setHeaderDisabled } = useHeader();
    const { loading: globalLoading = false } = useLoading();
    // TODO: figure how when to show title
    const titleVisible = useRef(false);

    const {
        videoMetadata = null,
        floatingVideo = null,
        floatingVideoSlots = null,
        removeFloatingVideoSlot,
        maxVideoWidth = null,
        fullscreen = false,
        setFullscreen = null,
        disableFixedSlot = null,
        enableFixedSlot = null,
        fixedSlot = null,
    } = useFloatingVideo();
    const { title = null } = videoMetadata || {};

    const hasFloatingVideo = floatingVideo !== null;
    const lastFloatingVideo = useRef(floatingVideo);
    const lastFullscreen = useRef(fullscreen);

    // If you set a fallback video slot
    const fallbackSlot = floatingVideoSlots.find(
        ({ fallback = false, disabled = false }) => fallback && !disabled,
    );

    // If theres a fixed slot somewhere in the page
    const { disabled: fixedSlotDisabled = false } = fixedSlot || {};

    // If disabled and single slot in page...
    const validSlots = floatingVideoSlots.filter(({ disabled = false }) => !disabled);
    const singleSlot = validSlots.length === 1;

    // The one that's currently close enough
    const currentSlot =
        floatingVideoSlots.reduce((selected, item) => {
            const {
                pctInViewport = 0,
                pctThreshold = 0,
                disabled = false,
                fixed = false,
            } = item || {};

            if (fixed) {
                return selected;
            }

            if (disabled) {
                return selected;
            }

            const { pctInViewport: previousPercentage = 0 } = selected || {};

            // The threshold for the switch
            if (
                (pctInViewport > (pctThreshold || defaultThreshold || 0.2) &&
                    pctInViewport > previousPercentage) ||
                singleSlot
            ) {
                return item;
            }

            return selected;
        }, null) ||
        fallbackSlot ||
        null;

    // The current slot
    const hasCurrentSlot = currentSlot !== null;

    // Hide the video and disable the slot
    const closeVideo = useCallback(() => {
        if (apiRef.current !== null) {
            apiRef.current.pause();
        }
        if (currentSlot !== null) {
            disableFixedSlot();
        }
        setFullscreen(false);
    }, [apiRef.current, currentSlot, removeFloatingVideoSlot, disableFixedSlot, setFullscreen]);

    const openVideo = useCallback(() => {
        if (apiRef.current !== null) {
            apiRef.current.play();
        }
        enableFixedSlot();
        setFullscreen(false);
    }, [apiRef.current, currentSlot, removeFloatingVideoSlot, enableFixedSlot, setFullscreen]);

    const clickVideo = useCallback(() => {
        if (!fullscreen) {
            if (apiRef.current !== null) {
                apiRef.current.play();
                apiRef.current.unmute();
            }
            setFullscreen(true);
        } else {
            if (apiRef.current !== null) {
                apiRef.current.mute();
                apiRef.current.play();
            }
            setFullscreen(false);
        }
    }, [apiRef.current, fullscreen, setFullscreen]);

    // Clear header states on fullscreen
    useEffect(() => {
        if (!fullscreen) {
            setScrollDirection('up');
            setHeaderHidden(false);
            setHeaderDisabled(false);
        } else {
            setScrollDirection('down');
            setHeaderHidden(true);
            setHeaderDisabled(true);
        }
    }, [fullscreen, setFullscreen, setHeaderHidden, setScrollDirection]);

    // Auto-pause the video if it gets removed or double down on play
    useEffect(() => {
        if (floatingVideo === null && apiRef.current !== null) {
            apiRef.current.pause();
        } else if (floatingVideo !== null && apiRef.current !== null && !apiRef.current.playing) {
            apiRef.current.play();
        }
    }, [floatingVideo, apiRef.current]);

    const lastMaxVideoWidth = useRef(maxVideoWidth);

    // The previous slots position
    const lastSlot = useRef(currentSlot);
    const { ref: lastSlotRef = null } = lastSlot.current || {};
    const { fixed: lastSlotFixed = false } = lastSlot.current || {};

    const {
        rect: currentSlotRect,
        fixed = false,
        disabled = false,
        name = null,
    } = currentSlot || {};

    const {
        x = 0,
        y = 0,
        scale = 0,
        width: currentWidth = null,
        ratio = 1,
    } = currentSlotRect || {};

    const getSpringProps = useCallback(() => {
        if (hasCurrentSlot) {
            const lastSlotRect =
                lastSlotRef !== null && lastSlotRef.current !== null
                    ? lastSlotRef.current.getBoundingClientRect()
                    : null;

            const lastSlotScale =
                lastSlotRect !== null ? lastSlotRect.width / (maxVideoWidth || 1) : null;

            // const lastSlotRatio =
            //     lastSlotRect !== null ? lastSlotRect.height / lastSlotRect.width : null;

            // Going fullscreen from another slot
            if (fullscreen && lastSlotRect !== null) {
                let to = null;
                const toWidth = Math.min(windowWidth, windowHeight / ratio);
                const toHeight = Math.min(windowHeight, toWidth * ratio);
                to = {
                    x: windowWidth / 2 - toWidth / 2,
                    y: windowHeight / 2 - toHeight / 2,
                    width: toWidth,
                    height: toHeight,
                    scale: 1,
                    position: 'fixed',
                };

                // Dont do it twice
                if (lastFullscreen.current === true) {
                    return to;
                }

                return {
                    from: {
                        x: lastSlotRect.x,
                        y: lastSlotRect.y,
                        scale: lastSlotScale,
                        width: maxVideoWidth,
                        height: maxVideoWidth * ratio,
                        position: lastSlotFixed ? 'fixed' : 'absolute',
                    },
                    to,
                };
            }

            // Default destination
            const to = {
                x,
                y,
                position: fixed ? 'fixed' : 'absolute',
                width: maxVideoWidth, // currentWidth,
                height: maxVideoWidth * ratio,
                scale,
            };

            // Its a resize
            if (lastSlotFixed && fixed) {
                return {
                    to,
                    immediate: true,
                };
            }

            // Page switch and suff
            if (lastSlotRect === null || lastSlotScale === null) {
                return to;
            }

            let from = {
                x: lastSlotRect.x,
                y: lastSlotRect.y,
                scale: lastSlotScale,
                width: maxVideoWidth,
                height: maxVideoWidth * ratio,
                position: lastSlotFixed ? 'fixed' : 'absolute',
            };

            // Switching from fixed to absolute smoothly
            if ((lastSlotFixed && !fixed) || (!fullscreen && !fixed)) {
                from = {
                    x: lastSlotRect.x,
                    y: lastSlotRect.y + (window.scrollY || window.pageYOffset),
                    scale: lastSlotScale,
                    width: maxVideoWidth, // lastSlotRect.width,
                    height: maxVideoWidth * ratio,
                    position: 'absolute',
                };
            }

            return { from, to };
        }
        return null;
    }, [
        hasCurrentSlot,
        x,
        y,
        scale,
        currentWidth,
        fixed,
        ratio,
        lastSlotFixed,
        maxVideoWidth,
        lastSlotRef,
        fullscreen,
        windowWidth,
        windowHeight,
        lastMaxVideoWidth,
        // lastFullscreen,
    ]);

    const [springStyle, springApi] = useSpring(() => getSpringProps());

    useEffect(() => {
        if (hasCurrentSlot) {
            if (!globalLoading) {
                springApi.start(getSpringProps());
            }
            lastFullscreen.current = fullscreen;
        }
    }, [
        springApi,
        hasCurrentSlot,
        x,
        y,
        scale,
        currentWidth,
        fixed,
        fullscreen,
        windowWidth,
        windowHeight,
        globalLoading,
    ]);

    if (floatingVideo !== null) {
        lastFloatingVideo.current = floatingVideo;
    }
    lastSlot.current = currentSlot;
    lastMaxVideoWidth.current = maxVideoWidth;

    const isVisible = maxVideoWidth > 0 && hasFloatingVideo && !disabled;

    useKeyboardKeys({
        [KEYS.ESCAPE]: closeVideo,
    });

    return (
        <>
            <animated.div
                className={classNames([
                    styles.container,
                    {
                        [className]: className !== null,
                        [styles.debug]: debug,
                        [styles[`slot-${name}`]]: name !== null,
                        [styles.isVisible]: hasFloatingVideo && currentSlot !== null,
                        [styles.isDisabled]: disabled && !fullscreen,
                        [styles.isFullscreen]: fullscreen,
                    },
                ])}
                style={springStyle}
            >
                {(isVisible && fixed) || fullscreen ? (
                    <div className={styles.actions}>
                        <CloseButton
                            className={classNames([
                                styles.close,
                                {
                                    [styles.fullscreen]: fullscreen,
                                },
                            ])}
                            iconClassName={styles.closeIcon}
                            onClick={fullscreen ? clickVideo : closeVideo}
                            color="#fff"
                            style={{
                                transform: !fullscreen
                                    ? `scale(${scale < 1 ? 1 / scale : 1})`
                                    : null,
                            }}
                        />
                    </div>
                ) : null}
                <button
                    type="button"
                    className={classNames([
                        styles.mainButton,
                        {
                            [styles.enabled]: !fullscreen,
                        },
                    ])}
                    onClick={clickVideo}
                />
                {maxVideoWidth > 0 ? (
                    <Embed
                        className={styles.embed}
                        embed={lastFloatingVideo.current}
                        apiRef={apiRef}
                        withSeekBar={fullscreen}
                        autoPlay
                        withoutControls
                        loop
                        initialMuted
                    />
                ) : null}
                {isVisible && title !== null ? (
                    <div
                        className={classNames([
                            styles.titleContainer,
                            {
                                [styles.visible]: titleVisible.current === true,
                            },
                        ])}
                    >
                        <span className={styles.title}>{title}</span>
                    </div>
                ) : null}
            </animated.div>
            <button
                type="button"
                className={classNames([
                    styles.modalButton,
                    {
                        [styles.isVisible]: fullscreen,
                    },
                ])}
                onClick={clickVideo}
            />
            <button
                type="button"
                className={classNames([
                    styles.open,
                    {
                        [styles.isVisible]: fixedSlotDisabled && hasFloatingVideo && !fullscreen,
                    },
                ])}
                onClick={openVideo}
            >
                <PlayIcon color="#fff" circleColor="transparent" />
            </button>
        </>
    );
};

FloatingVideo.propTypes = propTypes;
FloatingVideo.defaultProps = defaultProps;

export default FloatingVideo;
